/**
 * Stores the user info object in localStorage.
 * @param user_info - User info object (should be stringified).
 */
export const setLivisUserInfo = (user_info: any) => {
  localStorage.setItem("livis_user_info", user_info);
};

/**
 * Resets user info in localStorage but preserves language, theme, and brand settings.
 * Clears all localStorage, then restores preserved settings.
 */
export const resetLivisUserInfo = () => {
  const preservedLanguage = localStorage.getItem("livis_lang");
  const preservedTheme = localStorage.getItem("livis_theme");
  const preservedBrandColor = localStorage.getItem("livis_brand_color");
  const preservedBrandTheme = localStorage.getItem("livis_brand_theme");

  // Clear all localStorage items
  Object.keys(localStorage).forEach((key) => {
    if (key !== 'livis_cookie_consent') {
      localStorage.removeItem(key);
    }
  });
  sessionStorage.clear()

  // Restore preserved settings
  if (preservedLanguage) {
    localStorage.setItem("livis_lang", preservedLanguage);
  }
  if (preservedTheme) {
    localStorage.setItem("livis_theme", preservedTheme);
  }
  if (preservedBrandColor) {
    localStorage.setItem("livis_brand_color", preservedBrandColor);
  }
  if (preservedBrandTheme) {
    localStorage.setItem("livis_brand_theme", preservedBrandTheme);
  }
};

/**
 * Retrieves the user info object from localStorage.
 * @returns User info string or null.
 */
export const getLivisUserInfo = () => {
  return localStorage.getItem("livis_user_info");
};

/**
 * Retrieves the access token from the user info in localStorage.
 * @returns Access token string or null.
 */
export const getLivisToken = () => {
  let user_info: any = localStorage.getItem("livis_user_info");
  user_info = JSON.parse(user_info);
  if (user_info) {
    return user_info.access_token;
  } else {
    return null;
  }
};

/**
 * Retrieves the refresh token from the user info in localStorage.
 * @returns Refresh token string or false.
 */
export const getRefreshToken = () => {
  let user_info: any = localStorage.getItem("livis_user_info");
  user_info = JSON.parse(user_info);
  if (user_info) {
    return user_info.refresh_token;
  } else {
    return false;
  }
};

/**
 * Gets the alert status from localStorage.
 * @returns Parsed alert status or false.
 */
export const getAlertStatus = () => {
  const alert_status: any = localStorage.getItem("alert");
  if (alert_status) {
    return JSON.parse(alert_status);
  } else {
    return false;
  }
};

/**
 * Sets the alert status in localStorage.
 * @param alert_status - Alert status value.
 */
export const setNoAlertStatus = (alert_status: any) => {
  localStorage.setItem("alert", alert_status);
};

/**
 * Removes the alert status from localStorage.
 */
export const removeAlertStatus = () => {
  localStorage.removeItem("alert");
};

/**
 * Gets the selected language from localStorage.
 * @returns Language code string.
 */
export const getLivisLanguage = () => {
  const livis_lang: any = localStorage.getItem("livis_lang")
    ? localStorage.getItem("livis_lang")
    : "en";
  return livis_lang;
};

/**
 * Gets the selected theme from localStorage.
 * @returns Theme string.
 */
export const getLivistheme = () => {
  const livis_theme: any = localStorage.getItem("livis_theme")
    ? localStorage.getItem("livis_theme")
    : "light"; // Changed to return just "light" not "livis-light-theme"
  return livis_theme;
};

/**
 * Gets the brand color from localStorage.
 * @returns Brand color string.
 */
export const getLivisBrandColor = () => {
  const brand_color: any = localStorage.getItem("livis_brand_color")
    ? localStorage.getItem("livis_brand_color")
    : "#0857de";
  return brand_color;
};

/**
 * Gets the brand theme from localStorage.
 * @returns Brand theme string.
 */
export const getLivisBrandTheme = (): string => {
  return localStorage.getItem('livis_brand_theme') || '';
};

/**
 * Sets the theme in localStorage.
 * @param theme - Theme string.
 */
export const setLivisTheme = (theme: string) => {
  localStorage.setItem("livis_theme", theme);
  try {
    window.dispatchEvent(new CustomEvent('livis-theme-change', { detail: theme }));
  } catch (_) {
    // no-op
  }
};

/**
 * Sets the brand color in localStorage.
 * @param color - Brand color string.
 */
export const setLivisBrandColor = (color: string) => {
  localStorage.setItem("livis_brand_color", color);
};

/**
 * Sets the brand theme in localStorage.
 * @param theme - Brand theme string.
 */
export const setLivisBrandTheme = (theme: string) => {
  localStorage.setItem("livis_brand_theme", theme);
};

// export const setPartList = (part_list: any) => {
//   localStorage.setItem("part_list", part_list);
// };

// export const getPartList = () => {
//   const part_list: any = localStorage.getItem("part_list");
//   return JSON.parse(part_list);
// };

// export const setWorkstationType = (station_data: any) => {
//   localStorage.setItem("station_data", JSON.stringify(station_data));
// };

// export const getWorkstationData = () => {
//   const station_type: any = localStorage.getItem("station_data");
//   return JSON.parse(station_type);
// };

// export const setSelectedUsecase = (usecase_data: any) => {
//   localStorage.setItem("usecase_data", JSON.stringify(usecase_data));
// };

// export const getSelectedUsecase = () => {
//   const usecase_data: any = localStorage.getItem("usecase_data");
//   return JSON.parse(usecase_data);
// };

// export const setPLCControllerData = (plc_data: any) => {
//   localStorage.setItem("plc_controller", JSON.stringify(plc_data));
// };

// export const getPLCControllerData = () => {
//   const plc_controller: any = localStorage.getItem("plc_controller");
//   return JSON.parse(plc_controller);
// };

// export const setCobotControllerData = (plc_data: any) => {
//   localStorage.setItem("cobot_controller", JSON.stringify(plc_data));
// };

// export const getCobotControllerData = () => {
//   const plc_controller: any = localStorage.getItem("cobot_controller");
//   return JSON.parse(plc_controller);
// };
// export const removeCobotControllerData = () => {
//   const plc_controller: any = localStorage.removeItem("cobot_controller");
//   return;
// };

// export const setLocalSelectedPart = (part_data: any) => {
//   localStorage.setItem("part_data", JSON.stringify(part_data));
// };

// export const getLocalSelectedPart = () => {
//   const part_data: any = localStorage.getItem("part_data");
//   return JSON.parse(part_data);
// };

// export const setLocalRecipeStation = (stationData: any) => {
//   localStorage.setItem(
//     "recipe_inspection_station",
//     JSON.stringify(stationData)
//   );
// };

// export const getLocalRecipeStation = () => {
//   const station_data: any = localStorage.getItem("recipe_inspection_station");
//   return JSON.parse(station_data);
// };

// export const setDataTab = (tab: string) => {
//   localStorage.setItem("manage_data_tab", JSON.stringify(tab));
// };

// export const getDataTab = () => {
//   const tab_value: any = localStorage.getItem("manage_data_tab");
//   return JSON.parse(tab_value);
// };



export const setPartList = (part_list: any) => {
  sessionStorage.setItem("part_list", part_list);
};

/**
 * Retrieves the part list from sessionStorage.
 * @returns Parsed part list.
 */
export const getPartList = () => {
  const part_list: any = sessionStorage.getItem("part_list");
  return JSON.parse(part_list);
};

/**
 * Stores workstation data in sessionStorage.
 * @param station_data - Workstation data object.
 */
export const setWorkstationType = (station_data: any) => {
  sessionStorage.setItem("station_data", JSON.stringify(station_data));
};

/**
 * Retrieves workstation data from sessionStorage.
 * @returns Parsed workstation data.
 */
export const getWorkstationData = () => {
  const station_type: any = sessionStorage.getItem("station_data");
  return JSON.parse(station_type);
};

/**
 * Stores selected usecase data in sessionStorage, omitting certain fields.
 * @param usecase_data - Usecase data object.
 */
export const setSelectedUsecase = (usecase_data: any) => {
  try {
    const { usecase_thumbnail, running_experiment, experiments, ...rest } = usecase_data;
    console.log(rest);
    sessionStorage.setItem("usecase_data", JSON.stringify(rest));
  } catch (error) {
    console.error("Failed to set usecase data in sessionStorage:", error);
    localStorage.removeItem("usecase_data");
  }
};

/**
 * Retrieves selected usecase data from sessionStorage.
 * @returns Parsed usecase data.
 */
export const getSelectedUsecase = () => {
  const usecase_data: any = sessionStorage.getItem("usecase_data");
  return JSON.parse(usecase_data);
};

/**
 * Stores PLC controller data in sessionStorage.
 * @param plc_data - PLC controller data object.
 */
export const setPLCControllerData = (plc_data: any) => {
  sessionStorage.setItem("plc_controller", JSON.stringify(plc_data));
};

/**
 * Retrieves PLC controller data from sessionStorage.
 * @returns Parsed PLC controller data.
 */
export const getPLCControllerData = () => {
  const plc_controller: any = sessionStorage.getItem("plc_controller");
  return JSON.parse(plc_controller);
};

/**
 * Stores cobot controller data in sessionStorage.
 * @param plc_data - Cobot controller data object.
 */
export const setCobotControllerData = (plc_data: any) => {
  sessionStorage.setItem("cobot_controller", JSON.stringify(plc_data));
};

/**
 * Retrieves cobot controller data from sessionStorage.
 * @returns Parsed cobot controller data.
 */
export const getCobotControllerData = () => {
  const plc_controller: any = sessionStorage.getItem("cobot_controller");
  return JSON.parse(plc_controller);
};

/**
 * Removes cobot controller data from sessionStorage.
 */
export const removeCobotControllerData = () => {
  sessionStorage.removeItem("cobot_controller");
};

/**
 * Stores selected part data in sessionStorage.
 * @param part_data - Part data object.
 */
export const setLocalSelectedPart = (part_data: any) => {
  sessionStorage.setItem("part_data", JSON.stringify(part_data));
};

/**
 * Retrieves selected part data from sessionStorage.
 * @returns Parsed part data.
 */
export const getLocalSelectedPart = () => {
  const part_data: any = sessionStorage.getItem("part_data");
  return JSON.parse(part_data);
};

/**
 * Stores recipe inspection station data in sessionStorage.
 * @param stationData - Station data object.
 */
export const setLocalRecipeStation = (stationData: any) => {
  sessionStorage.setItem("recipe_inspection_station", JSON.stringify(stationData));
};

/**
 * Retrieves recipe inspection station data from sessionStorage.
 * @returns Parsed station data.
 */
export const getLocalRecipeStation = () => {
  const station_data: any = sessionStorage.getItem("recipe_inspection_station");
  return JSON.parse(station_data);
};

/**
 * Stores the selected data tab in sessionStorage.
 * @param tab - Tab string.
 */
export const setDataTab = (tab: string) => {
  sessionStorage.setItem("manage_data_tab", JSON.stringify(tab));
};

/**
 * Retrieves the selected data tab from sessionStorage.
 * @returns Parsed tab value.
 */
export const getDataTab = () => {
  const tab_value: any = sessionStorage.getItem("manage_data_tab");
  return JSON.parse(tab_value);
};

/**
 * Stores the annotation bucket type in sessionStorage.
 * @param bucket_type - Bucket type string.
 */
export const setAnnotationBucketType = (bucket_type: string) => {
  sessionStorage.setItem("annotation_bucket_type", bucket_type);
};

/**
 * Retrieves the annotation bucket type from sessionStorage.
 * @returns Bucket type string.
 */
export const getAnnotationBucketType = () => {
  return sessionStorage.getItem("annotation_bucket_type");
};

/**
 * Stores whether the inspection is of cobot type in sessionStorage.
 * @param value - Boolean value.
 */
export const setIsCobotTypeInspection = (value: boolean) => {
  sessionStorage.setItem("isCobotTypeInspection", JSON.stringify(value));
};

/**
 * Retrieves whether the inspection is of cobot type from sessionStorage.
 * @returns Boolean value or null.
 */
export const getIsCobotTypeInspection = () => {
  const value = sessionStorage.getItem("isCobotTypeInspection");
  return value ? JSON.parse(value) : null;
};

/**
 * Stores the layout configuration status in sessionStorage.
 * @param message - Status message or object.
 */
export const setLayoutConfigurationStatus = (message: any) => {
  sessionStorage.setItem("layout_configuration_status", JSON.stringify(message));
};

/**
 * Retrieves the layout configuration status from sessionStorage.
 * @returns Parsed status message or null.
 */
export const getLayoutConfigurationStatus = () => {
  const message = sessionStorage.getItem("layout_configuration_status");
  return message ? JSON.parse(message) : null;
};

/**
 * Removes the layout configuration status from sessionStorage.
 */
export const removeLayoutConfigurationStatus = () => {
  sessionStorage.removeItem("layout_configuration_status");
};

/**
 * Retrieves the pagination frequency from sessionStorage.
 * @returns Parsed pagination frequency or null.
 */
export const getPaginationFrequency = () => {
  const paginationFreq = sessionStorage.getItem("pagination_frequency");
  return paginationFreq ? JSON.parse(paginationFreq) : null;
};

/**
 * Removes the pagination frequency from sessionStorage.
 */
export const removePaginationFrequency = () => {
  sessionStorage.removeItem("pagination_frequency");
};

/**
 * Sets the pagination frequency in sessionStorage.
 * @param value - Pagination frequency value.
 */
export const setPaginationFrequency = (value: any) => {
  sessionStorage.setItem("pagination_frequency", JSON.stringify(value));
};

/**
 * Stores the manage data payload in sessionStorage.
 * @param payload - Payload object.
 */
export const setManageDataPayload = (payload: any) => {
  sessionStorage.setItem("manage_data_payload", JSON.stringify(payload));
};

/**
 * Retrieves the manage data payload from sessionStorage.
 * @returns Parsed payload or null.
 */
export const getManageDataPayload = () => {
  const payload = sessionStorage.getItem("manage_data_payload");
  return payload ? JSON.parse(payload) : null;
};

/**
 * Removes the manage data payload from sessionStorage.
 */
export const removeManageDataPayload = () => {
  sessionStorage.removeItem("manage_data_payload");
};

/**
 * Stores the reports analytics graphs in localStorage.
 * @param graphs - Array of graph data objects.
 */
export const setReportsAnalyticsGraphs = (graphs: any[]) => {
  try {
    localStorage.setItem("reports_analytics_graphs", JSON.stringify(graphs));
  } catch (error) {
    console.error("Failed to save graphs to localStorage:", error);
  }
};

/**
 * Retrieves the reports analytics graphs from localStorage.
 * @returns Array of graph data objects or empty array.
 */
export const getReportsAnalyticsGraphs = () => {
  try {
    const graphs = localStorage.getItem("reports_analytics_graphs");
    return graphs ? JSON.parse(graphs) : [];
  } catch (error) {
    console.error("Failed to retrieve graphs from localStorage:", error);
    return [];
  }
};

/**
 * Removes the reports analytics graphs from localStorage.
 */
export const removeReportsAnalyticsGraphs = () => {
  localStorage.removeItem("reports_analytics_graphs");
};

/**
 * Stores the reports analytics graph counter in localStorage.
 * @param counter - The current graph counter value.
 */
export const setReportsAnalyticsGraphCounter = (counter: number) => {
  try {
    localStorage.setItem("reports_analytics_graph_counter", counter.toString());
  } catch (error) {
    console.error("Failed to save graph counter to localStorage:", error);
  }
};

/**
 * Retrieves the reports analytics graph counter from localStorage.
 * @returns The graph counter value or 1 as default.
 */
export const getReportsAnalyticsGraphCounter = () => {
  try {
    const counter = localStorage.getItem("reports_analytics_graph_counter");
    return counter ? parseInt(counter, 10) : 1;
  } catch (error) {
    console.error("Failed to retrieve graph counter from localStorage:", error);
    return 1;
  }
};

/**
 * Stores the default view preference for Reports & Analytics in localStorage.
 * @param isDefault - Whether custom viewer should be the default view.
 */
export const setReportsAnalyticsDefaultView = (isDefault: boolean) => {
  try {
    localStorage.setItem("livis_reports_analytics_default_view", JSON.stringify(isDefault));
  } catch (error) {
    console.error("Error setting reports analytics default view:", error);
  }
};

/**
 * Retrieves the default view preference for Reports & Analytics from localStorage.
 * @returns Boolean indicating if custom viewer should be default view.
 */
export const getReportsAnalyticsDefaultView = () => {
  try {
    const defaultView = localStorage.getItem("livis_reports_analytics_default_view");
    return defaultView ? JSON.parse(defaultView) : false;
  } catch (error) {
    console.error("Error getting reports analytics default view:", error);
    return false;
  }
};

export const setPageDateRange = (pageKey: string, dateRange: any) => {
  try {
    localStorage.setItem(`date_range_${pageKey}`, JSON.stringify(dateRange));
  } catch (error) {
    console.error(`Error setting date range for ${pageKey}:`, error);
  }
};

/**
 * Retrieves the date range for a specific page from localStorage.
 * @param pageKey - A unique key for the page.
 * @returns The date range object or null if not found.
 */
export const getPageDateRange = (pageKey: string) => {
  try {
    const data = localStorage.getItem(`date_range_${pageKey}`);
    if (data) {
      const parsed = JSON.parse(data);
      
      const parseDate = (d: any) => {
        if (!d || d === "") return null;
        const date = new Date(d);
        return isNaN(date.getTime()) ? null : date;
      };

      // Convert string dates back to Date objects
      if (parsed.from_date) parsed.from_date = parseDate(parsed.from_date);
      if (parsed.to_date) parsed.to_date = parseDate(parsed.to_date);
      
      // If dates are invalid, fallback to default (return null)
      if (parsed.from_date === null || parsed.to_date === null) {
          return null;
      }

      return parsed;
    }
  } catch (error) {
    console.error(`Error getting date range for ${pageKey}:`, error);
  }
  return null;
};